import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Play, Lock, Loader2, Clapperboard, Users, CreditCard, CheckCircle2, AlertTriangle, Pencil } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api, formatPrice, moduleStreamUrl } from '../lib/api';
import { courseCover } from '../lib/covers';
import { UserAvatar } from '../components/UserAvatar';
import { useLanguage } from '../i18n';
import { useDominantColor } from '../lib/useDominantColor';
import { AmbientGlow } from '../components/AmbientGlow';
import { Skeleton } from '../components/ui/skeleton';

const fmtDuration = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
};

export default function CourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeModule, setActiveModule] = useState(null);
  const [busy, setBusy] = useState(false);
  const glowRgb = useDominantColor(course ? courseCover(course) : null);

  const load = async () => {
    try {
      const { data } = await api.get(`/academy/courses/${id}`);
      setCourse(data);
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const enrollFree = async () => {
    setBusy(true);
    try {
      await api.post(`/academy/courses/${id}/enroll`);
      toast.success(t('enrolled'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  const buyCourse = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/payments/checkout', {
        product_type: 'course',
        product_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || t('paymentError'));
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="ss-card p-10 text-center space-y-4 max-w-md mx-auto mt-8" data-testid="course-not-found">
        <AlertTriangle size={28} className="text-[#E9C46A] mx-auto" />
        <h1 className="font-display font-bold text-lg text-white">{t('notFoundTitle')}</h1>
        <button onClick={() => navigate('/academy')} className="text-sm text-white/70 hover:text-white font-mono" data-testid="course-back-academy">
          {t('backToAcademy')}
        </button>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="max-w-3xl mx-auto space-y-4 pt-2" data-testid="course-loading">
        <Skeleton className="h-52 w-full bg-white/[0.05] rounded-[24px]" />
        <Skeleton className="h-24 w-full bg-white/[0.05] rounded-[20px]" />
      </div>
    );
  }

  const price = course.price_cents > 0 ? formatPrice(course.price_cents, course.currency) : null;
  const unlocked = course.is_enrolled || course.is_mine;

  return (
    <div className="max-w-3xl mx-auto space-y-4 relative" data-testid="course-detail-page">
      <AmbientGlow rgb={glowRgb} intensity={0.2} className="-top-10 h-80" />

      <div className="flex items-center justify-between pt-1 relative">
        <button onClick={() => navigate('/academy')} className="flex items-center gap-1.5 text-sm text-white/55 hover:text-white transition-colors" data-testid="course-back-button">
          <ArrowLeft size={15} /> {t('backToAcademy')}
        </button>
        {course.is_mine && (
          <button onClick={() => navigate('/coach')} data-testid="course-edit-in-studio" className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white font-mono transition-colors">
            <Pencil size={12} /> {t('coachArea')}
          </button>
        )}
      </div>

      {/* hero */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="relative rounded-[24px] overflow-hidden border border-white/[0.07] bg-[#1A1A1A]" data-testid="course-hero">
        <div className="relative aspect-[16/8] sm:aspect-[16/6]">
          <div className="absolute inset-0" style={{ background: `linear-gradient(160deg, rgba(${glowRgb},0.35), #141414 80%)` }} />
          <img src={courseCover(course)} alt={course.title} className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} data-testid="course-hero-img" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, transparent 20%, rgba(0,0,0,0.6) 65%, rgba(0,0,0,0.95) 100%)' }} />
          <div className="absolute inset-x-0 bottom-0 p-5 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className={`rounded-[12px] px-2.5 py-1 font-display font-bold text-xs ${price ? 'bg-white text-[#121212]' : 'bg-black/55 text-white border border-white/25 backdrop-blur-md'}`} data-testid="course-price-badge">
                {price || t('free')}
              </span>
              {course.is_enrolled && (
                <span className="font-mono text-[10px] uppercase text-[#9BD770] bg-[rgba(155,215,112,0.12)] border border-[rgba(155,215,112,0.3)] rounded-[10px] px-2 py-0.5" data-testid="course-enrolled-badge">
                  {t('enrolled')}
                </span>
              )}
            </div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tight text-white leading-tight" data-testid="course-title">{course.title}</h1>
            <div className="flex items-center gap-3 text-white/60 text-xs">
              <span className="flex items-center gap-1.5">
                <UserAvatar src={course.coach?.picture} alt={course.coach?.name} size={20} rounded={8} />
                {t('courseBy')} <span className="text-white/85 font-medium">{course.coach?.name}</span>
              </span>
              <span className="flex items-center gap-1 font-mono text-[10px]"><Clapperboard size={10} /> {course.modules_count} {t('modules')}</span>
              <span className="flex items-center gap-1 font-mono text-[10px]"><Users size={10} /> {course.enrollments_count} {t('students')}</span>
            </div>
          </div>
        </div>
      </motion.div>

      {course.description && (
        <p className="text-sm text-white/65 leading-relaxed px-1" data-testid="course-description">{course.description}</p>
      )}

      {/* CTA */}
      {!unlocked && (
        course.price_cents > 0 ? (
          <button onClick={buyCourse} disabled={busy} data-testid="course-buy-button" className="ss-btn-primary w-full h-13 py-3.5 font-display font-bold text-base flex items-center justify-center gap-2.5">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CreditCard size={18} />} {t('buyCourse')} · {price}
          </button>
        ) : (
          <button onClick={enrollFree} disabled={busy} data-testid="course-enroll-button" className="ss-btn-primary w-full h-13 py-3.5 font-display font-bold text-base flex items-center justify-center gap-2.5">
            {busy ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle2 size={18} />} {t('enroll')}
          </button>
        )
      )}

      {/* active module player */}
      {activeModule && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-3" data-testid="course-module-player">
          <div className="rounded-[16px] overflow-hidden bg-black aspect-video">
            <video key={activeModule.module_id} src={moduleStreamUrl(activeModule.module_id)} controls autoPlay playsInline className="w-full h-full object-contain" data-testid="course-module-video" />
          </div>
          <div className="px-1.5 pt-2.5 pb-1">
            <div className="text-sm font-medium text-white">{activeModule.title}</div>
            {activeModule.description && <p className="text-xs text-white/50 mt-0.5">{activeModule.description}</p>}
          </div>
        </motion.div>
      )}

      {/* modules */}
      <div className="space-y-2" data-testid="course-modules-list">
        <h2 className="font-display font-bold text-lg tracking-tight text-white px-1">{t('lessons')}</h2>
        {(course.modules || []).map((m, i) => (
          <button
            key={m.module_id}
            onClick={() => !m.locked && setActiveModule(m)}
            disabled={m.locked}
            data-testid={`course-module-${m.module_id}`}
            className={`w-full ss-card p-3.5 flex items-center gap-3 text-left transition-colors ${
              m.locked ? 'opacity-60' : 'hover:border-white/25 cursor-pointer'
            } ${activeModule?.module_id === m.module_id ? 'border-white/30' : ''}`}
          >
            <span className={`w-10 h-10 rounded-[14px] flex items-center justify-center shrink-0 ${m.locked ? 'bg-white/[0.05] border border-white/10 text-white/40' : 'bg-[#22C55E] text-[#06210F]'}`}>
              {m.locked ? <Lock size={15} /> : <Play size={15} className="ml-0.5" />}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white/90 truncate">
                <span className="font-mono text-[10px] text-white/40 mr-1.5">{String(i + 1).padStart(2, '0')}</span>
                {m.title}
              </div>
              <div className="font-mono text-[10px] text-white/40 mt-0.5">
                {m.locked ? t('lockedLesson') : fmtDuration(m.duration)}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
